import { useState, useCallback, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Card from '@zenbusiness/zui/Card';
import CardContent from '@zenbusiness/zui/CardContent';
import Chip from '@zenbusiness/zui/Chip';
import IconButton from '@zenbusiness/zui/IconButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import Tooltip from '@zenbusiness/zui/Tooltip';

import * as Styled from './ProfileHeader.styles';
import ProfileHeaderLoadingState from './ProfileHeaderLoadingState';

function CopyButton({ label, value }) {
  const { formatMessage } = useIntl();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  const handleCopy = useCallback(async () => {
    if (!navigator.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard write failed — leave icon unchanged
    }
  }, [value]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copyLabel = formatMessage({ id: 'dashboard.enterpriseHome.profileHeader.copyButtonLabel' }, { label });
  const tooltipTitle = copied
    ? formatMessage({ id: 'dashboard.enterpriseHome.profileHeader.copiedTooltip' })
    : formatMessage({ id: 'dashboard.enterpriseHome.profileHeader.copyTooltip' });

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <IconButton
        label={copyLabel}
        aria-label={copyLabel}
        size="small"
        onClick={handleCopy}
        sx={{ color: copied ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)', padding: '2px' }}
      >
        {copied ? <CheckIcon sx={{ fontSize: '0.875rem' }} /> : <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />}
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

function StatusItem({ label, value, isEnterprisePlan, copyable, isPending }) {
  return (
    <Stack orientation="horizontal" alignItems="center" gap={1}>
      <Text size="xs" weight="medium" color="white">
        {`${label}:`}
      </Text>
      {isEnterprisePlan ? (
        <Chip size="sm" label={value} color="secondary" icon={<RocketLaunchIcon />} iconPosition="end" />
      ) : (
        <Stack orientation="horizontal" alignItems="center" gap={0}>
          <Text size="xs" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {value}
          </Text>
          {copyable && !isPending && <CopyButton label={label} value={value} />}
        </Stack>
      )}
    </Stack>
  );
}

StatusItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  isEnterprisePlan: PropTypes.bool,
  copyable: PropTypes.bool,
  isPending: PropTypes.bool,
};

function ProfileHeader({ statusItems, loading, hasError, name, nameInitials, formationDate }) {
  const { formatMessage, formatDate } = useIntl();

  if (loading) {
    return <ProfileHeaderLoadingState />;
  }
  if (hasError) {
    return null;
  }

  return (
    <Card color="black">
      <CardContent size="lg">
        <Styled.Content>
          <Stack orientation="horizontal" alignItems="center" gap={2}>
            <Styled.Avatar>{nameInitials}</Styled.Avatar>
            <Styled.TextContainer>
              <Text color="white" weight="medium" wrap="truncate">
                {name}
              </Text>
              {formationDate && (
                <Text sx={{ color: 'rgba(255, 255, 255, 0.6)' }} size="sm" wrap="truncate">
                  {formatMessage(
                    { id: 'dashboard.enterpriseHome.profileHeader.formationDate' },
                    { date: formatDate(formationDate, { month: 'numeric', day: 'numeric', year: 'numeric' }) },
                  )}
                </Text>
              )}
            </Styled.TextContainer>
          </Stack>
          <Styled.ProfileWrapper>
            {statusItems?.length > 0 && (
              <Stack orientation="horizontal" alignItems="center" gap={2} flexWrap="wrap">
                {statusItems.map((item) => (
                  <StatusItem key={item.label} {...item} />
                ))}
              </Stack>
            )}
          </Styled.ProfileWrapper>
        </Styled.Content>
      </CardContent>
    </Card>
  );
}

ProfileHeader.propTypes = {
  statusItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
      isEnterprisePlan: PropTypes.bool,
      copyable: PropTypes.bool,
      isPending: PropTypes.bool,
    }),
  ),
  loading: PropTypes.bool,
  hasError: PropTypes.bool,
  name: PropTypes.string,
  nameInitials: PropTypes.string,
  formationDate: PropTypes.string,
};

export default ProfileHeader;
